import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Material UI Core Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';

// Import the integrated exam interface
import IntegratedExamInterface from './IntegratedExamInterface';

// Material UI Icons
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HistoryIcon from '@mui/icons-material/History';
import SchoolIcon from '@mui/icons-material/School';
import { API_ENDPOINTS, authenticatedApiRequest } from '../config/api';

function StudentDashboard() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [activeSection, setActiveSection] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [upcomingExams, setUpcomingExams] = useState([]);
  const [completedExams, setCompletedExams] = useState([]);
  const [results, setResults] = useState([]);
  const [currentExamId, setCurrentExamId] = useState(null);
  const [submittedExams, setSubmittedExams] = useState(new Set());
  const [resultDetailsOpen, setResultDetailsOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [studentUpcomingSubjectFilter, setStudentUpcomingSubjectFilter] = useState('');
  const [studentUpcomingDateFilter, setStudentUpcomingDateFilter] = useState('');
  const [studentCompletedSubjectFilter, setStudentCompletedSubjectFilter] = useState('');
  const [studentCompletedDateFilter, setStudentCompletedDateFilter] = useState('');

  const drawerWidth = 240;
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in and has student role
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');

    if (!storedUser || !storedUser.username) {
      // Redirect to login if not logged in
      navigate('/');
      return;
    }

    if (storedUser.role !== 'student') {
      // Redirect to appropriate dashboard based on role
      if (storedUser.role === 'admin') {
        navigate('/admin-dashboard');
      } else if (storedUser.role === 'teacher') {
        navigate('/teacher-dashboard');
      }
      return;
    }

    setUser(storedUser);

    // Fetch data initially
    fetchUpcomingExams(storedUser);
    fetchCompletedExams(storedUser);
    fetchResults(storedUser);

    // Set up interval to refresh exam data every 30 seconds
    // This ensures exam status updates in real-time
    const refreshInterval = setInterval(() => {
      if (activeSection !== 'exam') { // Don't refresh while taking exam
        fetchUpcomingExams(storedUser);
        fetchCompletedExams(storedUser);
      }
    }, 30000); // 30 seconds

    return () => {
      clearInterval(refreshInterval);
    };
  }, [navigate, activeSection]);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setMobileOpen(false);
  };
  
  const fetchUpcomingExams = async (user) => {
    try {
      setLoading(true);
      console.log('Fetching upcoming exams for student:', user.username);
      
      const response = await authenticatedApiRequest(
        API_ENDPOINTS.STUDENT_UPCOMING_EXAMS,
        user,
        { method: 'GET' }
      );
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch upcoming exams: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Received exam data:', data);
      
      // Filter for exams that are upcoming OR ongoing (can be started)
      // Include both 'upcoming' and 'ongoing' status, exclude only 'completed'
      const upcomingExamsList = (data.exams || []).filter(exam => {
        const examStatus = getExamStatus(exam);
        return (examStatus === 'upcoming' || examStatus === 'ongoing');
      });
      
      setUpcomingExams(upcomingExamsList);
      console.log('Set upcoming exams:', upcomingExamsList);

      // Check which exams have been submitted
      const submittedExamIds = new Set();
      for (const exam of upcomingExamsList) {
        const hasSubmitted = await checkExamSubmission(exam._id, user.username);
        if (hasSubmitted) {
          submittedExamIds.add(exam._id);
        }
      }
      setSubmittedExams(submittedExamIds);
      console.log('Submitted exams:', submittedExamIds);
    } catch (error) {
      console.error('Error fetching upcoming exams:', error);
      showSnackbar('Failed to load upcoming exams: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCompletedExams = async (user) => {
    try {
      setLoading(true);
      console.log('Fetching completed exams for student:', user.username);
      
      const response = await authenticatedApiRequest(
        API_ENDPOINTS.STUDENT_COMPLETED_EXAMS,
        user,
        { method: 'GET' }
      );
      
      console.log('Completed exams response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch completed exams: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Received completed exam data:', data);
      
      setCompletedExams(data.exams || []);
    } catch (error) {
      console.error('Error fetching completed exams:', error);
      showSnackbar('Failed to load completed exams: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchResults = async (user) => {
    try {
      setLoading(true);
      console.log('Fetching results for student:', user.username);
      
      const response = await authenticatedApiRequest(
        API_ENDPOINTS.STUDENT_RESULTS,
        user,
        { method: 'GET' }
      );
      
      console.log('Results API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch results: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Received results data:', data);
      console.log('Number of results:', data.results ? data.results.length : 0);
      
      if (data.results && data.results.length > 0) {
        console.log('First result:', data.results[0]);
      } else {
        console.log('No results received from API');
      }
      
      setResults(data.results || []);
    } catch (error) {
      console.error('Error fetching results:', error);
      showSnackbar('Failed to load results: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };
  
  const canStartExam = (exam) => {
    const now = new Date();
    const examDate = new Date(exam.date);
    const examEndTime = new Date(examDate.getTime() + (exam.duration * 60000));

    // Can only start if current time is within exam window
    return now >= examDate && now <= examEndTime;
  };

  const getExamStatus = (exam) => {
    const now = new Date();
    const examDate = new Date(exam.date);
    const examEndTime = new Date(examDate.getTime() + (exam.duration * 60000));

    if (now < examDate) {
      return 'upcoming';
    } else if (now >= examDate && now <= examEndTime) {
      return 'ongoing';
    } else {
      return 'completed';
    }
  };

  const checkExamSubmission = async (examId, userId) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.EXAM_SUBMISSIONS}?exam_id=${examId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`Checking submissions for exam ${examId}, user ${userId}:`, data.submissions);

        // Check if this student has already submitted this exam (completed or terminated)
        const studentSubmission = data.submissions?.find(sub => {
          const isThisStudent = sub.student_username === userId || sub.user_id === userId;
          const isSubmitted = sub.status === 'completed' || sub.status === 'terminated';
          console.log(`Checking submission: user=${sub.student_username || sub.user_id}, status=${sub.status}, isThisStudent=${isThisStudent}, isSubmitted=${isSubmitted}`);
          return isThisStudent && isSubmitted;
        });

        const hasSubmitted = !!studentSubmission;
        console.log(`Exam ${examId} submission check result for ${userId}: ${hasSubmitted}`);
        return hasSubmitted;
      }
      console.log(`Failed to check submissions for exam ${examId}: HTTP ${response.status}`);
      return false;
    } catch (error) {
      console.error('Error checking exam submission:', error);
      return false;
    }
  };

  const handleStartExam = async (examId) => {
    console.log('🚀 handleStartExam called for exam:', examId);
    console.log('🚀 Current submittedExams set:', submittedExams);
    console.log('🚀 Is exam in submitted set?', submittedExams.has(examId));

    // Find the exam to validate timing
    const exam = upcomingExams.find(e => e._id === examId);
    if (!exam) {
      showSnackbar('Exam not found', 'error');
      return;
    }

    // Check if student has already submitted this exam (use cached status)
    if (submittedExams.has(examId)) {
      console.log('🚫 Exam already submitted, blocking start');
      showSnackbar('You have already submitted this exam and cannot retake it.', 'error');
      return;
    }

    // Double-check with backend to ensure submission status is current
    console.log('🔍 Double-checking submission status with backend...');
    const hasSubmitted = await checkExamSubmission(examId, user.username);
    if (hasSubmitted) {
      console.log('🚫 Backend confirms exam was submitted, updating local state and blocking start');
      setSubmittedExams(prev => new Set([...prev, examId]));
      showSnackbar('You have already submitted this exam and cannot retake it.', 'error');
      return;
    }
    console.log('✅ Backend confirms exam not submitted, proceeding with start');

    // Check if exam can be started
    if (!canStartExam(exam)) {
      const examStatus = getExamStatus(exam);
      if (examStatus === 'upcoming') {
        showSnackbar('Exam has not started yet. Please wait until the scheduled time.', 'warning');
      } else if (examStatus === 'completed') {
        showSnackbar('Exam time has expired.', 'error');
      }
      return;
    }

    // Store the exam information for the integrated exam interface
    if (user) {
      localStorage.setItem('currentExam', examId);
      localStorage.setItem('userId', user.username);
      localStorage.setItem('examId', examId);
      localStorage.setItem('studentName', user.name || user.username);
      localStorage.setItem('studentEmail', user.email || '');

      console.log('Starting exam with student info:', {
        userId: user.username,
        examId: examId,
        studentName: user.name || user.username,
        studentEmail: user.email || ''
      });

      // Set the current exam ID and switch to exam interface
      setCurrentExamId(examId);
      setActiveSection('exam');
    } else {
      console.error('No user information available when starting exam');
      showSnackbar('User information not available', 'error');
    }
  };

  const handleExamComplete = (result) => {
    console.log('📝 Exam completed, refreshing data...');

    // Refresh the exam lists to reflect completion
    if (user) {
      fetchUpcomingExams(user);
      fetchCompletedExams(user);
      fetchResults(user);
    }

    // Return to dashboard
    setCurrentExamId(null);
    setActiveSection('dashboard');

    showSnackbar('Exam completed successfully!', 'success');
  };

  const handleReturnToDashboard = () => {
    console.log('🏠 Returning to dashboard, refreshing data...');

    // Refresh data when returning to dashboard (in case exam was submitted/terminated)
    if (user) {
      fetchUpcomingExams(user);
      fetchCompletedExams(user);
      fetchResults(user);
    }

    setCurrentExamId(null);
    setActiveSection('dashboard');
  };

  const handleViewResultDetails = (result) => {
    setSelectedResult(result);
    setResultDetailsOpen(true);
  };

  const drawer = (
    <Box sx={{ bgcolor: '#f5f5f5', height: '100%' }}>
      <Box sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0277bd' }}>
          ViGiLANT
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Student Portal
        </Typography>
      </Box>

      <List component="nav" sx={{ pt: 1 }}>
        <ListItemButton
          selected={activeSection === 'dashboard'}
          onClick={() => handleSectionChange('dashboard')}
        >
          <ListItemIcon>
            <DashboardIcon color={activeSection === 'dashboard' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>

        <ListItemButton
          selected={activeSection === 'profile'}
          onClick={() => handleSectionChange('profile')}
        >
          <ListItemIcon>
            <PersonIcon color={activeSection === 'profile' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="My Profile" />
        </ListItemButton>

        <ListItemButton
          selected={activeSection === 'upcoming'}
          onClick={() => handleSectionChange('upcoming')}
        >
          <ListItemIcon>
            <CalendarTodayIcon color={activeSection === 'upcoming' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Upcoming Exams" />
        </ListItemButton>

        <ListItemButton
          selected={activeSection === 'completed'}
          onClick={() => handleSectionChange('completed')}
        >
          <ListItemIcon>
            <HistoryIcon color={activeSection === 'completed' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Completed Exams" />
        </ListItemButton>

        <ListItemButton
          selected={activeSection === 'results'}
          onClick={() => handleSectionChange('results')}
        >
          <ListItemIcon>
            <AssessmentIcon color={activeSection === 'results' ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Results" />
        </ListItemButton>

        <Divider sx={{ my: 2 }} />

        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Box>
  );
  
  // Check if we're in exam mode to hide sidebar and app bar
  const isExamMode = activeSection === 'exam' && currentExamId;

  return (
    <>
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <CssBaseline />

        {/* App Bar - Hidden during exam */}
        {!isExamMode && (
          <AppBar
            position="fixed"
            sx={{
              width: { sm: `calc(100% - ${drawerWidth}px)` },
              ml: { sm: `${drawerWidth}px` },
              bgcolor: '#0277bd',
              boxShadow: 2
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                {activeSection === 'dashboard' && 'Dashboard'}
                {activeSection === 'profile' && 'My Profile'}
                {activeSection === 'upcoming' && 'Upcoming Exams'}
                {activeSection === 'completed' && 'Completed Exams'}
                {activeSection === 'results' && 'Results'}
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {/* Sidebar - Hidden during exam */}
        {!isExamMode && (
          <Box
            component="nav"
            sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            aria-label="mailbox folders"
          >
            {/* Mobile drawer */}
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
              }}
            >
              {drawer}
            </Drawer>

            {/* Desktop drawer */}
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
              }}
              open
            >
              {drawer}
            </Drawer>
          </Box>
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: isExamMode ? 0 : 3,
            width: isExamMode ? '100%' : { sm: `calc(100% - ${drawerWidth}px)` },
            mt: isExamMode ? 0 : { xs: 7, sm: 8 },
            height: isExamMode ? '100vh' : '100%',
            bgcolor: isExamMode ? 'inherit' : '#f9f9f9',
            overflow: isExamMode ? 'hidden' : 'auto',
            boxShadow: 0
          }}
        >
          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <Box>
              {/* Welcome Card */}
              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Welcome, {user?.name || user?.username}!
                </Typography>
                <Typography variant="body1">
                  This is your student dashboard where you can view your upcoming exams,
                  check your completed exams, and see your results.
                </Typography>
              </Paper>

              {/* Exam Statistics Section */}
              <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Exam Stats
                </Typography>

                <Grid container spacing={2}>
                  {/* Upcoming Exams */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ bgcolor: '#2196f3', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Upcoming Exams</Typography>
                          <CalendarTodayIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{upcomingExams.length}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Completed Exams */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ bgcolor: '#4caf50', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Completed Exams</Typography>
                          <CheckCircleIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{completedExams.length}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Results Available */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ bgcolor: '#ff9800', color: 'white', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1">Results Available</Typography>
                          <AssessmentIcon />
                        </Box>
                        <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>{results.length}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>

              {/* Next Exam Cards: Show all upcoming exams as cards, sorted by date, excluding submitted */}
              {upcomingExams.filter(exam => !submittedExams.has(exam._id))
                .filter(exam =>
                  (!studentUpcomingSubjectFilter || exam.subject === studentUpcomingSubjectFilter) &&
                  (!studentUpcomingDateFilter || exam.date.slice(0, 10) === studentUpcomingDateFilter)
                ).length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Your Next Exam{upcomingExams.filter(exam => !submittedExams.has(exam._id)).length > 1 ? 's' : ''}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        console.log('🔄 Manual refresh triggered');
                        if (user) {
                          fetchUpcomingExams(user);
                          fetchCompletedExams(user);
                          fetchResults(user);
                        }
                      }}
                      sx={{ minWidth: 'auto' }}
                    >
                      🔄 Refresh
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    {upcomingExams
                      .filter(exam => !submittedExams.has(exam._id))
                      .filter(exam =>
                        (!studentUpcomingSubjectFilter || exam.subject === studentUpcomingSubjectFilter) &&
                        (!studentUpcomingDateFilter || exam.date.slice(0, 10) === studentUpcomingDateFilter)
                      )
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .map((exam) => (
                        <Grid item xs={12} md={6} lg={4} key={exam._id}>
                          <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="h5">
                                {exam.title}
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                {new Date(exam.date).toLocaleString()}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                Duration: {exam.duration} minutes
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label={submittedExams.has(exam._id)
                                    ? 'Submitted'
                                    : getExamStatus(exam) === 'upcoming' ? 'Not Started'
                                    : getExamStatus(exam) === 'ongoing' ? 'Available Now'
                                    : 'Ended'}
                                  color={submittedExams.has(exam._id)
                                    ? 'default'
                                    : getExamStatus(exam) === 'upcoming' ? 'warning'
                                    : getExamStatus(exam) === 'ongoing' ? 'success'
                                    : 'error'}
                                  size="small"
                                />
                              </Box>
                            </Box>
                            <Button
                              variant="contained"
                              color={submittedExams.has(exam._id) ? "success" : "primary"}
                              onClick={() => handleStartExam(exam._id)}
                              startIcon={<AssignmentIcon />}
                              disabled={!canStartExam(exam) || submittedExams.has(exam._id)}
                            >
                              {submittedExams.has(exam._id) ? 'SUBMITTED' :
                               canStartExam(exam) ? 'Start Exam' :
                               getExamStatus(exam) === 'upcoming' ? 'Not Started Yet' :
                               'Exam Ended'}
                            </Button>
                          </Paper>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
          
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3 }}>
                My Profile
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  minWidth: '200px'
                }}>
                  <Avatar 
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      bgcolor: 'primary.main',
                      fontSize: '3rem',
                      mb: 2
                    }}
                  >
                    {user?.name ? user.name.charAt(0).toUpperCase() : user?.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="h6">
                    {user?.name || user?.username}
                  </Typography>
                  <Chip 
                    label="Student" 
                    color="primary" 
                    icon={<SchoolIcon />} 
                    sx={{ mt: 1 }}
                  />
                </Box>
                
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Username
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {user?.username}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Full Name
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {user?.name || 'Not provided'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {user?.email || 'Not provided'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        Created By
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {user?.created_by || 'Admin'}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Account Statistics
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4">
                            {upcomingExams.length + completedExams.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Exams
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4">
                            {completedExams.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Completed
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4">
                            {results.length > 0 ? 
                              Math.round(results.reduce((acc, result) => acc + result.score, 0) / results.length) + '%' : 
                              'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Avg. Score
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}
          
          {/* Upcoming Exams Section */}
          {activeSection === 'upcoming' && (
            <Box>
              {/* Filter UI for students */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  select
                  label="Subject"
                  value={studentUpcomingSubjectFilter}
                  onChange={e => setStudentUpcomingSubjectFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(upcomingExams.map(e => e.subject))).map(subject => (
                    <MenuItem key={subject} value={subject}>
                      {subject?.charAt(0).toUpperCase() + subject?.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Date"
                  type="date"
                  value={studentUpcomingDateFilter}
                  onChange={e => setStudentUpcomingDateFilter(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { if (user) { fetchUpcomingExams(user); fetchCompletedExams(user); fetchResults(user); } }}
                  sx={{ minWidth: 'auto' }}
                >
                  Refresh
                </Button>
              </Box>
              {/* Table of Upcoming Exams */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">Upcoming Exams</Typography>
                  <Typography variant="body2" color="text.secondary">Sorted by earliest first</Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Exam Title</TableCell>
                        <TableCell>Date & Time</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {upcomingExams
                        .filter(exam => !submittedExams.has(exam._id))
                        .filter(exam =>
                          (!studentUpcomingSubjectFilter || exam.subject === studentUpcomingSubjectFilter) &&
                          (!studentUpcomingDateFilter || exam.date.slice(0, 10) === studentUpcomingDateFilter)
                        )
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((exam) => (
                          <TableRow key={exam._id}>
                            <TableCell>{exam.title}</TableCell>
                            <TableCell>{new Date(exam.date).toLocaleString()}</TableCell>
                            <TableCell>{exam.duration} minutes</TableCell>
                            <TableCell>
                              <Chip
                                label={getExamStatus(exam) === 'upcoming' ? 'Not Started' : getExamStatus(exam) === 'ongoing' ? 'Available Now' : 'Ended'}
                                color={getExamStatus(exam) === 'upcoming' ? 'warning' : getExamStatus(exam) === 'ongoing' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{exam.created_by || '-'}</TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                color={getExamStatus(exam) === 'ongoing' ? 'primary' : 'inherit'}
                                onClick={() => handleStartExam(exam._id)}
                                startIcon={<AssignmentIcon />}
                                disabled={!canStartExam(exam)}
                              >
                                {getExamStatus(exam) === 'upcoming' ? 'NOT STARTED YET' : getExamStatus(exam) === 'ongoing' ? 'START' : 'ENDED'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
          
          {/* Completed Exams Section */}
          {activeSection === 'completed' && (
            <Box>
              {/* Filter UI for students */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  select
                  label="Subject"
                  value={studentCompletedSubjectFilter}
                  onChange={e => setStudentCompletedSubjectFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(completedExams.map(e => e.subject))).map(subject => (
                    <MenuItem key={subject} value={subject}>
                      {subject?.charAt(0).toUpperCase() + subject?.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Date"
                  type="date"
                  value={studentCompletedDateFilter}
                  onChange={e => setStudentCompletedDateFilter(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { if (user) { fetchUpcomingExams(user); fetchCompletedExams(user); fetchResults(user); } }}
                  sx={{ minWidth: 'auto' }}
                >
                  Refresh
                </Button>
              </Box>
              {/* Completed Exams Table in Paper */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">Completed Exams</Typography>
                  <Typography variant="body2" color="text.secondary">Sorted by latest first</Typography>
                </Box>
                {completedExams.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Exam Title</TableCell>
                          <TableCell>Date Completed</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Sort completed exams by date descending (latest first) */}
                        {[...completedExams]
                          .filter(exam =>
                            (!studentCompletedSubjectFilter || exam.subject === studentCompletedSubjectFilter) &&
                            (!studentCompletedDateFilter || (exam.completed_date ? exam.completed_date.slice(0, 10) : exam.date.slice(0, 10)) === studentCompletedDateFilter)
                          )
                          .sort((a, b) => {
                            const dateA = a.date ? new Date(a.date) :
                                         a.completed_date ? new Date(a.completed_date) :
                                         a.created_at ? new Date(a.created_at) : new Date(0);
                            const dateB = b.date ? new Date(b.date) :
                                         b.completed_date ? new Date(b.completed_date) :
                                         b.created_at ? new Date(b.created_at) : new Date(0);
                            return dateB - dateA; // Sort descending (newest first)
                          })
                          .map((exam) => (
                            <TableRow key={exam._id}>
                              <TableCell>{exam.title}</TableCell>
                              <TableCell>{new Date(exam.completed_date || exam.date).toLocaleString()}</TableCell>
                              <TableCell>{exam.duration} minutes</TableCell>
                              <TableCell>
                                <Chip
                                  label="Completed"
                                  color="success"
                                  size="small"
                                  icon={<CheckCircleIcon />}
                                />
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleSectionChange('results')}
                                >
                                  View Result
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body1">No completed exams found.</Typography>
                )}
              </Paper>
            </Box>
          )}
          
          {/* Results Section */}
          {activeSection === 'results' && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">
                  Exam Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sorted by latest first
                </Typography>
              </Box>
              
              {results.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Exam Title</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Sort results by submission date descending (latest first) */}
                      {[...results].sort((a, b) => {
                        // Get date values with fallbacks
                        const dateValueA = a.submitted_at || a.date;
                        const dateValueB = b.submitted_at || b.date;

                        // Parse dates with error handling
                        let dateA, dateB;
                        try {
                          dateA = dateValueA ? new Date(dateValueA) : new Date(0);
                          if (isNaN(dateA.getTime())) dateA = new Date(0);
                        } catch (e) {
                          console.error('Error parsing dateA in student results:', dateValueA, e);
                          dateA = new Date(0);
                        }

                        try {
                          dateB = dateValueB ? new Date(dateValueB) : new Date(0);
                          if (isNaN(dateB.getTime())) dateB = new Date(0);
                        } catch (e) {
                          console.error('Error parsing dateB in student results:', dateValueB, e);
                          dateB = new Date(0);
                        }

                        return dateB.getTime() - dateA.getTime(); // Sort descending (newest first)
                      }).map((result) => (
                        <TableRow key={result._id}>
                          <TableCell>{result.exam_title || 'Unknown Exam'}</TableCell>
                          <TableCell>
                            {result.submitted_at ? new Date(result.submitted_at).toLocaleString() :
                             result.date ? new Date(result.date).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body1"
                              color={result.score >= 70 ? 'success.main' : result.score >= 40 ? 'warning.main' : 'error.main'}
                              fontWeight="bold"
                            >
                              {result.score !== undefined ? `${Math.round(result.score)}%` : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {result.status === 'terminated' ? (
                              <Chip
                                label="Terminated"
                                color="error"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            ) : result.score !== undefined ? (
                              <Chip
                                label={result.score >= 40 ? "Passed" : "Failed"}
                                color={result.score >= 40 ? "success" : "error"}
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="Submitted"
                                color="primary"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewResultDetails(result)}
                              startIcon={<VisibilityIcon />}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1">
                  No results available yet.
                </Typography>
              )}
            </Paper>
          )}

          {/* Integrated Exam Interface - Full Screen */}
          {activeSection === 'exam' && currentExamId && (
            <Box sx={{
              width: '100%',
              height: '100vh',
              position: isExamMode ? 'fixed' : 'relative',
              top: isExamMode ? 0 : 'auto',
              left: isExamMode ? 0 : 'auto',
              zIndex: isExamMode ? 1300 : 'auto', // Above other components
              bgcolor: 'background.default',
              overflow: 'hidden' // Prevent double scrollbars
            }}>
              <IntegratedExamInterface
                examId={currentExamId}
                onExamComplete={handleExamComplete}
                onReturnToDashboard={handleReturnToDashboard}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Result Details Dialog */}
      <Dialog
        open={resultDetailsOpen}
        onClose={() => setResultDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon color="primary" sx={{ mr: 1 }} />
            Exam Result Details
          </Box>
          <IconButton onClick={() => setResultDetailsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedResult && (
            <Box>
              {/* Exam Summary */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    📋 {selectedResult.exam_title || 'Exam Results'}
                  </Typography>
                  <Chip
                    label={`${selectedResult.score !== undefined ? Math.round(selectedResult.score) : 0}%`}
                    sx={{
                      bgcolor: selectedResult.score >= 70 ? '#4caf50' : selectedResult.score >= 40 ? '#ff9800' : '#f44336',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      px: 2,
                      py: 1
                    }}
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    {selectedResult.status === 'terminated' ? (
                      <Box>
                        <Chip
                          label="TERMINATED"
                          color="error"
                          size="small"
                          sx={{ mt: 0.5, fontWeight: 'bold' }}
                        />
                        <Typography variant="body2" color="error" sx={{ mt: 1, fontStyle: 'italic' }}>
                          Exam was terminated due to {selectedResult.termination_reason || 'maximum warnings reached'}
                        </Typography>
                        {selectedResult.warning_count && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Total warnings: {selectedResult.warning_count}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Chip
                        label={selectedResult.score >= 40 ? "PASSED" : "FAILED"}
                        color={selectedResult.score >= 40 ? "success" : "error"}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Submitted On
                    </Typography>
                    <Typography variant="body1">
                      {selectedResult.submitted_at ? new Date(selectedResult.submitted_at).toLocaleString() :
                       selectedResult.date ? new Date(selectedResult.date).toLocaleString() : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Questions Attempted
                    </Typography>
                    <Typography variant="body1">
                      {selectedResult.answers ? Object.values(selectedResult.answers).filter(a => a && a.toString().trim() !== '').length : 0} / {selectedResult.total_questions || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Questions Unattempted
                    </Typography>
                    <Typography variant="body1">
                      {selectedResult.total_questions ? (selectedResult.total_questions - (selectedResult.answers ? Object.values(selectedResult.answers).filter(a => a && a.toString().trim() !== '').length : 0)) : 0} / {selectedResult.total_questions || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Correct Answers
                    </Typography>
                    <Typography variant="body1">
                      {selectedResult.correct_answers || 0} / {selectedResult.total_questions || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Question-by-Question Breakdown */}
              {(() => {
                const questions = selectedResult.questions_with_answers || selectedResult.questions || [];

                if (questions.length > 0) {
                  return (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        📝 Question-by-Question Review
                      </Typography>

                      {questions.map((question, index) => {
                        const studentAnswer = selectedResult.answers ? selectedResult.answers[question.question_id || question._id] : question.student_answer;
                        const correctAnswer = question.correctAnswer || question.correct_answer;
                        const isCorrect = studentAnswer === correctAnswer;
                        const wasAttempted = studentAnswer && studentAnswer.toString().trim() !== '';

                        return (
                          <Paper
                            key={question.question_id || question._id || index}
                            elevation={1}
                            sx={{
                              p: 2,
                              mb: 2,
                              border: isCorrect ? '2px solid #4caf50' : wasAttempted ? '2px solid #f44336' : '2px solid #ff9800'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
                                Question {index + 1}: {question.questionText || question.question_text}
                              </Typography>
                              <Chip
                                label={isCorrect ? "Correct" : wasAttempted ? "Incorrect" : "Not Attempted"}
                                color={isCorrect ? "success" : wasAttempted ? "error" : "warning"}
                                size="small"
                              />
                            </Box>

                            {question.options && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Options:
                                </Typography>
                                {question.options.map((option, optIndex) => (
                                  <Box
                                    key={optIndex}
                                    sx={{
                                      p: 1,
                                      mb: 0.5,
                                      borderRadius: 1,
                                      bgcolor: option === correctAnswer ? '#e8f5e8' :
                                               option === studentAnswer ? '#ffebee' :
                                               'transparent',
                                      border: option === correctAnswer ? '1px solid #4caf50' :
                                              option === studentAnswer ? '1px solid #f44336' :
                                              '1px solid #e0e0e0'
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {String.fromCharCode(65 + optIndex)}. {option}
                                      {option === correctAnswer && ' ✓ (Correct)'}
                                      {option === studentAnswer && option !== correctAnswer && ' ✗ (Your Answer)'}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 2 }}>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Questions Attempted
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                  {selectedResult.answers ? Object.values(selectedResult.answers).filter(a => a && a.toString().trim() !== '').length : 0} / {selectedResult.total_questions || 0}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Questions Unattempted
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                  {selectedResult.total_questions ? (selectedResult.total_questions - (selectedResult.answers ? Object.values(selectedResult.answers).filter(a => a && a.toString().trim() !== '').length : 0)) : 0} / {selectedResult.total_questions || 0}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Correct Answers
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                  {selectedResult.correct_answers || 0} / {selectedResult.total_questions || 0}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        );
                      })}
                    </Box>
                  );
                } else {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        No detailed question information available for this result.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This might be an older submission created before detailed question data was stored.
                      </Typography>
                    </Box>
                  );
                }
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResultDetailsOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default StudentDashboard;
